import { useState } from "react";
import { Mail, Phone, MapPin, Calendar, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";
import TrustBar from "@/components/TrustBar";

const TIDYCAL = "https://tidycal.com/book-a-meeting-with-schalk-coetzee";

const SUBJECTS = [
    "General Inquiry",
    "Audit / Independent Review",
    "Annual Financial Statements",
    "Tax & SARS",
    "Payroll",
    "Pricing Question",
    "Xero Migration",
    "Estates & Trusts",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: SUBJECTS[0],
        message: "",
    });
    const [status, setStatus] = useState("idle"); // idle | loading | success
    const [errors, setErrors] = useState({});

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    const handleEmailChange = (e) => {
        const val = e.target.value;
        setForm((f) => ({ ...f, email: val }));
        if (val.length === 0) {
            setErrors((er) => ({ ...er, email: undefined }));
        } else if (!EMAIL_RE.test(val.trim())) {
            setErrors((er) => ({ ...er, email: "Please enter a valid email address (e.g. name@example.co.za)." }));
        } else {
            setErrors((er) => ({ ...er, email: undefined }));
        }
    };

    const validate = () => {
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your full name.";
        if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(form.email.trim()))
            er.email = "Please enter a valid email address (e.g. name@example.co.za).";
        if (form.message.trim().length < 90)
            er.message = "Please describe in more detail how we can assist you so that we can prepare for the meeting.";
        return er;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("loading");
        try {
            await submitInquiry({ ...form, source: "general" });
            setStatus("success");
            toast.success("Inquiry sent — we will be in touch shortly.");
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Something went wrong. Please try again or call us directly.");
        }
    };

    return (
        <div data-testid="page-contact">
            {/* Hero */}
            <section className="bg-white border-b border-tca-line relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-60" />
                <div className="container-tca relative py-20 md:py-28">
                    <div className="max-w-3xl animate-fade-up">
                        <p className="overline text-tca-orange mb-4">Contact · Established 2015 · CA(SA) · RA</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                            Two ways
                            <span className="block text-tca-orange">to get started.</span>
                        </h1>
                        <p className="mt-6 text-tca-mute text-lg leading-relaxed max-w-2xl">
                            Send us a general inquiry and our back office will respond within one business day — or skip the wait and book a virtual meeting straight on the team's calendar.
                        </p>
                    </div>
                </div>
            </section>

            {/* Two-column: form + meeting */}
            <section className="bg-white">
                <div className="container-tca py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Inquiry form */}
                    <div className="lg:col-span-7">
                        <div className="border border-tca-line bg-white">
                            <div className="border-b border-tca-line px-7 lg:px-9 py-6 flex items-center justify-between">
                                <div>
                                    <p className="overline mb-1.5 text-tca-orange">Option 1</p>
                                    <h2 className="font-display font-extrabold text-2xl text-tca-ink">Send a General Inquiry</h2>
                                </div>
                                <Mail className="w-6 h-6 text-tca-orange" />
                            </div>

                            {status === "success" ? (
                                <div className="px-7 lg:px-9 py-12 text-center" data-testid="contact-success">
                                    <CheckCircle2 className="w-14 h-14 text-tca-orange mx-auto mb-5" />
                                    <h3 className="font-display font-extrabold text-2xl text-tca-ink mb-2">Thank you, {form.name.split(" ")[0]}!</h3>
                                    <p className="text-tca-mute leading-relaxed max-w-md mx-auto">
                                        Your inquiry has been received and routed to <strong className="text-tca-ink">admin@tcauditors.co.za</strong>. Expect a reply within one business day.
                                    </p>
                                    <button
                                        onClick={() => {
                                            setStatus("idle");
                                            setForm({ name: "", email: "", phone: "", company: "", subject: SUBJECTS[0], message: "" });
                                        }}
                                        data-testid="contact-send-another"
                                        className="btn-secondary mt-7 inline-flex"
                                    >
                                        Send another
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="p-7 lg:p-9 space-y-5" data-testid="contact-form">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="overline mb-2 block">Full name</label>
                                            <input
                                                value={form.name}
                                                onChange={update("name")}
                                                data-testid="contact-input-name"
                                                className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                                                placeholder="Jan van der Merwe"
                                            />
                                            {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                                        </div>
                                        <div>
                                            <label className="overline mb-2 block">Email</label>
                                            <input
                                                type="email"
                                                value={form.email}
                                                onChange={handleEmailChange}
                                                data-testid="contact-input-email"
                                                className={`w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 bg-white transition-colors ${
                                                    errors.email
                                                        ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                                                        : "border-tca-line focus:border-tca-orange focus:ring-tca-orangeSoft"
                                                }`}
                                                placeholder="jan@example.co.za"
                                            />
                                            {errors.email && <p className="text-xs text-red-600 mt-1" data-testid="email-error">{errors.email}</p>}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="overline mb-2 block">Phone (optional)</label>
                                            <input
                                                value={form.phone}
                                                onChange={update("phone")}
                                                data-testid="contact-input-phone"
                                                className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                                                placeholder="072 123 4567"
                                            />
                                        </div>
                                        <div>
                                            <label className="overline mb-2 block">Company (optional)</label>
                                            <input
                                                value={form.company}
                                                onChange={update("company")}
                                                data-testid="contact-input-company"
                                                className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                                                placeholder="Your business"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="overline mb-2 block">Subject</label>
                                        <select
                                            value={form.subject}
                                            onChange={update("subject")}
                                            data-testid="contact-input-subject"
                                            className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                                        >
                                            {SUBJECTS.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="overline mb-2 block">How can we help?</label>
                                        <textarea
                                            rows={5}
                                            value={form.message}
                                            onChange={update("message")}
                                            data-testid="contact-input-message"
                                            className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft resize-none"
                                            placeholder="Tell us about your business and what you need help with..."
                                        />
                                        {errors.message && <p className="text-base font-bold text-red-600 mt-2" data-testid="message-error">{errors.message}</p>}
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={status === "loading"}
                                        data-testid="contact-submit-btn"
                                        className="btn-primary w-full sm:w-auto group disabled:opacity-60"
                                    >
                                        {status === "loading" ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                                            </>
                                        ) : (
                                            <>
                                                Send Inquiry
                                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                                            </>
                                        )}
                                    </button>
                                    <p className="text-xs text-tca-mute pt-2">
                                        We respect your privacy. Submissions are routed to admin@tcauditors.co.za only.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Schedule meeting + contact info */}
                    <div className="lg:col-span-5 space-y-6">
                        <div className="border border-tca-orange bg-tca-orangeSoft/40 p-7 lg:p-9" data-testid="contact-schedule-card">
                            <p className="overline mb-1.5 text-tca-orange">Option 2</p>
                            <h2 className="font-display font-extrabold text-2xl text-tca-ink mb-3">Schedule a Virtual Meeting</h2>
                            <p className="text-sm text-tca-mute leading-relaxed mb-6">
                                Book a 30-minute Zoom directly on Schalk Coetzee's calendar. Both free discovery calls and paid technical consultations are available.
                            </p>
                            <a
                                href={TIDYCAL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-tidycal-btn"
                                className="btn-primary group w-full"
                            >
                                <Calendar className="w-4 h-4" />
                                Book on TidyCal
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                            </a>
                        </div>

                        <div className="border border-tca-line bg-white p-7 lg:p-9 space-y-5" data-testid="contact-info-card">
                            <p className="overline">Direct lines</p>
                            <ul className="space-y-4 text-sm">
                                <li className="flex items-start gap-3">
                                    <MapPin className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" />
                                    <span className="text-tca-ink leading-relaxed">42 Boet Kruger Street<br />Meyerton, Gauteng, 1961</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Mail className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" />
                                    <a href="mailto:admin@tcauditors.co.za" className="text-tca-ink hover:text-tca-orange transition-colors font-semibold">
                                        admin@tcauditors.co.za
                                    </a>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Phone className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" />
                                    <span className="text-tca-ink">
                                        <span className="block text-[10px] uppercase tracking-[0.2em] text-tca-mute mb-0.5">Reception</span>
                                        <a href="https://wa.me/27794617545" target="_blank" rel="noopener noreferrer" className="hover:text-tca-orange transition-colors font-semibold">079 461 7545</a>
                                        <span className="ml-2 text-[9px] font-bold text-green-600 border border-green-400 bg-green-50 px-1.5 py-0.5 rounded">WA</span>
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Phone className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" />
                                    <span className="text-tca-ink">
                                        <span className="block text-[10px] uppercase tracking-[0.2em] text-tca-mute mb-0.5">Back Office</span>
                                        <a href="https://wa.me/27826993003" target="_blank" rel="noopener noreferrer" className="hover:text-tca-orange transition-colors font-semibold">082 699 3003</a>
                                        <span className="ml-2 text-[9px] font-bold text-green-600 border border-green-400 bg-green-50 px-1.5 py-0.5 rounded">WA</span>
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Phone className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" />
                                    <span className="text-tca-ink">
                                        <span className="block text-[10px] uppercase tracking-[0.2em] text-tca-mute mb-0.5">Help Desk</span>
                                        <a href="tel:0600151110" className="hover:text-tca-orange transition-colors font-semibold">060 015 1110</a>
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <TrustBar />

            {/* Map / location */}
            <section className="bg-tca-fog border-t border-tca-line">
                <div className="container-tca py-12 md:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-4">
                            <p className="overline mb-3">Visit us</p>
                            <h2 className="font-display font-extrabold text-3xl text-tca-ink leading-[1.05]">42 Boet Kruger Street, Meyerton.</h2>
                            <p className="mt-4 text-sm text-tca-mute leading-relaxed">
                                Our office is open Mondays to Fridays. Walk-ins welcome by prior appointment.
                            </p>
                            <div className="mt-6 pt-5 border-t border-tca-line">
                                <p className="overline mb-3">Office Hours</p>
                                <ul className="space-y-1.5 text-sm text-tca-ink" data-testid="office-hours">
                                    {[
                                        ["Mondays", "08:00 to 17:00"],
                                        ["Tuesdays", "08:00 to 17:00"],
                                        ["Wednesdays", "08:00 to 17:00"],
                                        ["Thursdays", "08:00 to 17:00"],
                                        ["Fridays", "08:00 to 14:00"],
                                    ].map(([day, hours]) => (
                                        <li key={day} className="flex justify-between">
                                            <span className="font-bold">{day}</span>
                                            <span className="text-tca-mute">{hours}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="lg:col-span-8 aspect-[16/8] border border-tca-line bg-white overflow-hidden">
                            <iframe
                                title="TC Auditors location"
                                data-testid="contact-map"
                                src="https://www.google.com/maps?q=42+Boet+Kruger+Street,+Meyerton,+Gauteng,+1961&output=embed"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
